import React, { useEffect, useRef, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Box, Typography, Chip, CircularProgress, Alert,
} from '@mui/material';
import { Close, Phone, LocalShipping, MyLocation, Refresh } from '@mui/icons-material';
import { io } from 'socket.io-client';
import api from '../services/api';

const RAW_API = process.env.REACT_APP_API_URL || `${window.location.origin}/api`;
const SOCKET_URL = RAW_API.replace(/\/api\/?$/, '');

let kakaoLoadPromise = null;
function loadKakaoMaps() {
  if (kakaoLoadPromise) return kakaoLoadPromise;
  kakaoLoadPromise = new Promise((resolve, reject) => {
    const tryLoad = () => {
      if (window.kakao && window.kakao.maps && typeof window.kakao.maps.load === 'function') {
        window.kakao.maps.load(() => {
          if (window.kakao.maps.LatLng) resolve(window.kakao);
          else reject(new Error('Kakao Maps SDK 초기화 실패'));
        });
        return true;
      }
      return false;
    };
    if (tryLoad()) return;
    const start = Date.now();
    const check = setInterval(() => {
      if (tryLoad()) { clearInterval(check); return; }
      if (Date.now() - start > 10000) {
        clearInterval(check);
        reject(new Error('지도 로드 시간 초과'));
      }
    }, 200);
  });
  return kakaoLoadPromise;
}

const formatTime = (dt) => {
  if (!dt) return '-';
  const d = new Date(dt);
  if (isNaN(d.getTime())) return '-';
  const diff = Math.floor((Date.now() - d.getTime()) / 1000);
  if (diff < 60) return `${diff}초 전`;
  if (diff < 3600) return `${Math.floor(diff / 60)}분 전`;
  return `${d.getHours()}:${String(d.getMinutes()).padStart(2, '0')}`;
};

export default function DriverLocationDialog({ open, onClose, order }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);
  const [mapReady, setMapReady] = useState(false);

  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const overlayRef = useRef(null);
  const polylineRef = useRef(null);
  const socketRef = useRef(null);
  const originMarkerRef = useRef(null);
  const destMarkerRef = useRef(null);
  const routePolylineRef = useRef(null);
  const fittedRef = useRef(false);
  const originLatLngRef = useRef(null);
  const destLatLngRef = useRef(null);

  // 데이터 로드
  const load = async () => {
    if (!order?.order_id) return;
    setLoading(true);
    setError('');
    try {
      const res = await api.get(`/customer/orders/${order.order_id}/driver-location`);
      setData(res.data.data);
    } catch (err) {
      setError(err.response?.data?.message || '위치를 불러올 수 없습니다.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    load();
  }, [open, order?.order_id]);

  // Kakao Map 초기화 (dialog 가 열릴 때)
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    loadKakaoMaps()
      .then((kakao) => {
        if (cancelled || !mapContainerRef.current) return;
        const map = new kakao.maps.Map(mapContainerRef.current, {
          center: new kakao.maps.LatLng(37.5665, 126.9780),
          level: 6,
        });
        mapRef.current = map;
        setMapReady(true);
        // resize 트리거 (dialog 안에서 사이즈 변경 대응)
        setTimeout(() => kakao.maps.event.trigger(map, 'resize'), 200);
      })
      .catch((err) => setError(err.message || '지도 로드 실패'));
    return () => {
      cancelled = true;
      setMapReady(false);
      mapRef.current = null;
      [markerRef, overlayRef, polylineRef, originMarkerRef, destMarkerRef, routePolylineRef].forEach((r) => {
        if (r.current) { r.current.setMap(null); r.current = null; }
      });
      fittedRef.current = false;
      originLatLngRef.current = null;
      destLatLngRef.current = null;
    };
  }, [open]);

  // 상차/하차지 마커 + 직선 경로 — DB 좌표 우선, 없으면 Geocoder fallback
  useEffect(() => {
    if (!mapReady || !window.kakao || !mapRef.current || !order) return;
    if (originMarkerRef.current && destMarkerRef.current) return; // 한 번만
    const kakao = window.kakao;
    const map = mapRef.current;

    const makePinOverlay = (latLng, label, color) => {
      const div = document.createElement('div');
      div.style.cssText = `background:${color};color:#fff;padding:3px 8px;border-radius:3px;font-size:11px;font-weight:700;white-space:nowrap;box-shadow:0 2px 4px rgba(0,0,0,.2);transform:translateY(-30px);`;
      div.textContent = label;
      return new kakao.maps.CustomOverlay({ position: latLng, content: div, yAnchor: 1, map });
    };

    const tryFromData = (lat, lng) => {
      const la = Number(lat), ln = Number(lng);
      if (Number.isFinite(la) && Number.isFinite(ln) && (la !== 0 || ln !== 0)) {
        return new kakao.maps.LatLng(la, ln);
      }
      return null;
    };

    const geocode = (addr) => new Promise((resolve) => {
      if (!addr) return resolve(null);
      if (!kakao.maps.services?.Geocoder) return resolve(null);
      const geocoder = new kakao.maps.services.Geocoder();
      geocoder.addressSearch(addr, (result, status) => {
        if (status === kakao.maps.services.Status.OK && result?.[0]) {
          resolve(new kakao.maps.LatLng(parseFloat(result[0].y), parseFloat(result[0].x)));
        } else {
          resolve(null);
        }
      });
    });

    (async () => {
      // 1순위: API 응답의 좌표
      let oLatLng = tryFromData(data?.origin_lat, data?.origin_lng);
      let dLatLng = tryFromData(data?.destination_lat, data?.destination_lng);
      // 2순위: 주소 geocode
      if (!oLatLng) oLatLng = await geocode(order.origin_address);
      if (!dLatLng) dLatLng = await geocode(order.destination_address);

      if (oLatLng) {
        originLatLngRef.current = oLatLng;
        originMarkerRef.current = new kakao.maps.Marker({ position: oLatLng, map });
        makePinOverlay(oLatLng, '상차', '#2e7d32');
      }
      if (dLatLng) {
        destLatLngRef.current = dLatLng;
        destMarkerRef.current = new kakao.maps.Marker({ position: dLatLng, map });
        makePinOverlay(dLatLng, '하차', '#c62828');
      }
      if (oLatLng && dLatLng) {
        routePolylineRef.current = new kakao.maps.Polyline({
          path: [oLatLng, dLatLng],
          strokeWeight: 3, strokeColor: '#666', strokeOpacity: 0.5, strokeStyle: 'shortdash',
          map,
        });
      }
      if ((oLatLng || dLatLng) && !data?.current) {
        const bounds = new kakao.maps.LatLngBounds();
        if (oLatLng) bounds.extend(oLatLng);
        if (dLatLng) bounds.extend(dLatLng);
        map.setBounds(bounds);
      }
    })();
    // eslint-disable-next-line
  }, [mapReady, order, data]);

  // Socket.IO 실시간 업데이트
  useEffect(() => {
    if (!open || !order?.order_id) return;
    const socket = io(SOCKET_URL, { transports: ['websocket', 'polling'] });
    socketRef.current = socket;
    const eventName = `order-${order.order_id}-location`;
    socket.on(eventName, (payload) => {
      setData(prev => ({
        ...(prev || {}),
        current: {
          latitude: payload.latitude,
          longitude: payload.longitude,
          updated_at: new Date().toISOString(),
        },
        path: [...((prev && prev.path) || []), {
          latitude: payload.latitude,
          longitude: payload.longitude,
          recorded_at: new Date().toISOString(),
        }],
      }));
    });
    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
  }, [open, order?.order_id]);

  // 데이터 변경 시 지도 갱신
  useEffect(() => {
    if (!mapReady || !window.kakao || !mapRef.current || !data?.current) return;
    const kakao = window.kakao;
    const map = mapRef.current;
    const { latitude, longitude } = data.current;
    const pos = new kakao.maps.LatLng(latitude, longitude);

    // 마커
    if (!markerRef.current) {
      markerRef.current = new kakao.maps.Marker({ position: pos, map });
    } else {
      markerRef.current.setPosition(pos);
    }

    // 오버레이 (차량 정보)
    const label = `${data.driver?.name || '운전자'} · ${data.driver?.vehicle_number || ''}`;
    if (!overlayRef.current) {
      const div = document.createElement('div');
      div.style.cssText = 'background:#1565c0;color:#fff;padding:4px 10px;border-radius:14px;font-size:12px;font-weight:600;white-space:nowrap;box-shadow:0 2px 6px rgba(0,0,0,.25);transform:translateY(-36px);';
      div.textContent = label;
      overlayRef.current = new kakao.maps.CustomOverlay({
        position: pos, content: div, yAnchor: 1, map,
      });
    } else {
      overlayRef.current.setPosition(pos);
      const c = overlayRef.current.getContent();
      if (c && c.textContent !== label) c.textContent = label;
    }

    // 경로 폴리라인
    if (data.path && data.path.length > 1) {
      const linePath = data.path
        .filter(p => p.latitude && p.longitude)
        .map(p => new kakao.maps.LatLng(p.latitude, p.longitude));
      if (polylineRef.current) polylineRef.current.setMap(null);
      polylineRef.current = new kakao.maps.Polyline({
        path: linePath,
        strokeWeight: 4,
        strokeColor: '#1565c0',
        strokeOpacity: 0.7,
        strokeStyle: 'solid',
        map,
      });
    }

    // 첫 운전자 위치가 도착한 시점에 상하차+운전자 모두 포함하는 bounds 로 한 번만 fit
    if (!fittedRef.current) {
      const bounds = new kakao.maps.LatLngBounds();
      bounds.extend(pos);
      if (originLatLngRef.current) bounds.extend(originLatLngRef.current);
      if (destLatLngRef.current) bounds.extend(destLatLngRef.current);
      map.setBounds(bounds);
      fittedRef.current = true;
    } else {
      // 이후에는 운전자 위치만 따라가도록 부드럽게 panTo
      map.panTo(pos);
    }
  }, [mapReady, data]);

  if (!order) return null;
  const driver = data?.driver;
  const current = data?.current;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" PaperProps={{ sx: { height: '85vh' } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', bgcolor: '#1565c0', color: '#fff', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', minWidth: 0 }}>
          <LocalShipping />
          <Typography variant="h6" fontWeight={700}>실시간 추적</Typography>
          {current && (
            <Chip
              icon={<MyLocation sx={{ color: '#fff !important' }} />}
              label={`마지막 갱신: ${formatTime(current.updated_at)}`}
              size="small"
              sx={{
                ml: 1,
                bgcolor: 'rgba(255,255,255,0.18)',
                color: '#fff',
                borderColor: 'rgba(255,255,255,0.5)',
                '& .MuiChip-icon': { color: '#fff' },
              }}
              variant="outlined"
            />
          )}
        </Box>
        <Button onClick={onClose} sx={{ color: '#fff', minWidth: 0 }}><Close /></Button>
      </DialogTitle>
      <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column' }}>
        {/* 운전자 정보 카드 */}
        <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderBottom: '1px solid #e0e0e0' }}>
          {loading && !data ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <CircularProgress size={20} />
              <Typography variant="body2">위치 정보 불러오는 중...</Typography>
            </Box>
          ) : driver ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="body1" fontWeight={700}>{driver.name} 기사님</Typography>
                <Typography variant="body2" color="text.secondary">{driver.vehicle_number}</Typography>
              </Box>
              {driver.phone && (
                <Button variant="outlined" size="small" startIcon={<Phone />} href={`tel:${driver.phone}`}>
                  {driver.phone}
                </Button>
              )}
              <Button variant="text" size="small" startIcon={<Refresh />} onClick={load}>새로고침</Button>
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">배차된 운전자가 없습니다.</Typography>
          )}
        </Box>

        {error && <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>}

        {/* 지도 */}
        <Box ref={mapContainerRef} sx={{ flex: 1, width: '100%', minHeight: 400, bgcolor: '#e0e0e0' }} />

        {!current && !loading && !error && (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              아직 운전자 위치 정보가 없습니다.<br />
              배송이 시작되면 실시간 위치가 표시됩니다.
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>닫기</Button>
      </DialogActions>
    </Dialog>
  );
}
